// src/components/VersionModal.js
// Shows app version + EAS Update ID so testers can share exact build info.
import React, { useCallback } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Share,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '../theme';

// expo-updates is only meaningful in native builds. On web or when running in
// Expo Go without an OTA channel it returns undefined / null gracefully.
let Updates;
try {
  Updates = require('expo-updates');
} catch (_) {
  Updates = null;
}

// expo-constants provides the app version from app.json at build time, so it
// stays accurate even when the version field is bumped without touching JS.
let Constants;
try {
  Constants = require('expo-constants').default;
} catch (_) {
  Constants = null;
}

function getAppVersion() {
  return (
    Constants?.expoConfig?.version ??
    Constants?.manifest?.version ??
    Constants?.manifest2?.runtimeVersion ??
    'unknown'
  );
}

function getUpdateInfo() {
  if (!Updates) return { updateId: null, channel: null, isEmbedded: true, isEnabled: false };
  // Updates.updateId is sourced from the expo-updates native module, which reads
  // persisted update state from disk at every cold boot.  It is NOT an in-memory
  // value: a force-quit followed by a cold relaunch will produce the same UUID as
  // a background resume, as long as no new OTA update has been applied in between.
  // Reading the property at render time (rather than caching it in module scope)
  // ensures the displayed value always reflects the current boot state.
  return {
    updateId: Updates.updateId ?? null,
    channel: Updates.channel ?? null,
    isEmbedded: Updates.isEmbeddedLaunch ?? true,
    // isEnabled is false in Expo Go / dev builds; true in production/preview builds
    isEnabled: Updates.isEnabled ?? false,
  };
}

export default function VersionModal({ visible, onClose }) {
  const { updateId, channel, isEmbedded, isEnabled } = getUpdateInfo();
  const appVersion = getAppVersion();

  const updateLabel = !isEnabled
    ? 'disabled (dev build)'
    : isEmbedded
    ? 'embedded (no OTA)'
    : updateId
    ? updateId
    : 'unknown';

  // The OTA Update ID (UUID) is placed first so iOS share-sheet truncation
  // cannot drop it — any truncation would cut from the bottom instead.
  const shareText = [
    `OTA Update: ${updateLabel}`,
    `SleepMixBlueprint v${appVersion}`,
    channel ? `Channel: ${channel}` : null,
    `Platform: ${Platform.OS}`,
  ]
    .filter(Boolean)
    .join('\n');

  const handleShare = useCallback(async () => {
    try {
      await Share.share({ message: shareText });
    } catch (_) {}
  }, [shareText]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      {/*
        Backdrop touch-isolation strategy (iOS):
        - React Native's <Modal> renders in a separate native UIWindow, so touches
          inside the modal cannot reach the navigation content (MixerScreen tiles,
          LibraryScreen list) in the window below.  The backdrop TouchableOpacity
          therefore consumes the tap at the JS level; nothing propagates through.
        - onStartShouldSetResponder on the backdrop prevents any native-level
          responder from escaping upward on older RN/iOS combos.
        - onStartShouldSetResponder on the card ensures taps on the card's own
          content claim the responder there, so they never reach the backdrop's
          onPress handler and accidentally trigger onClose.
      */}
      <TouchableOpacity
        style={styles.backdrop}
        activeOpacity={1}
        onPress={onClose}
        onStartShouldSetResponder={() => true}
      >
        <View style={styles.card} onStartShouldSetResponder={() => true}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>App Info</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close" size={20} color={Colors.textMuted} />
            </TouchableOpacity>
          </View>

          {/* Rows */}
          <Row label="Version" value={`v${appVersion}`} />
          <Row label="OTA Update" value={updateLabel} mono />
          {channel ? <Row label="Channel" value={channel} /> : null}
          <Row label="Platform" value={Platform.OS} />

          {/* Share button */}
          <TouchableOpacity style={styles.shareBtn} onPress={handleShare}>
            <Ionicons name="share-outline" size={16} color={Colors.bg} style={{ marginRight: 6 }} />
            <Text style={styles.shareBtnText}>Share with developer</Text>
          </TouchableOpacity>

          <Text style={styles.hint}>Long-press ℹ️ to open this panel again</Text>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

function Row({ label, value, mono }) {
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={[styles.rowValue, mono && styles.mono]} numberOfLines={2} selectable>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  card: {
    width: '100%',
    backgroundColor: Colors.surface ?? '#1a1d24',
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: Colors.border ?? '#2a2d36',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    color: Colors.textBright ?? '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: Colors.border ?? '#2a2d36',
  },
  rowLabel: {
    color: Colors.textMuted ?? '#888',
    fontSize: 13,
    flex: 1,
  },
  rowValue: {
    color: Colors.textBright ?? '#fff',
    fontSize: 13,
    flex: 2,
    textAlign: 'right',
  },
  mono: {
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 11,
  },
  shareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.accent ?? '#6c63ff',
    borderRadius: 10,
    paddingVertical: 10,
    marginTop: 16,
  },
  shareBtnText: {
    color: Colors.bg ?? '#0d0f14',
    fontSize: 14,
    fontWeight: '600',
  },
  hint: {
    color: Colors.textMuted ?? '#888',
    fontSize: 11,
    textAlign: 'center',
    marginTop: 10,
  },
});
